import * as React from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Building2 } from "lucide-react";
import type { Plan } from "@/lib/finance-plans";
import { estimateEligibility } from "@/lib/eligibility";
import { track } from "@/lib/analytics";
import { createBnplCheckoutSession } from "@/lib/checkout";
import { useToast } from "@/hooks/use-toast";
import { trackReferralClick, generateSessionId } from "@/lib/referral-tracking";
import BestForLabel, { PROVIDER_LABELS } from "@/components/BestForLabel";

// ─── Plan-based selection (education / healthcare) ──────────────────

interface PlanSelectionProps {
  amountAED: number;
  recommendedPlans: Plan[];
  onBack: () => void;
  onSelect: (plan: Plan) => void;
}

export default function BNPLProviderSelection({ amountAED, recommendedPlans, onBack, onSelect }: PlanSelectionProps) {
  const handlePlanSelect = (plan: Plan) => {
    track("plan_recommended", { planId: plan.id, amountAED });
    onSelect(plan);
  };
  return (
    <Card className="p-6">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" onClick={onBack}>
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <div>
          <h3 className="text-xl font-heading font-semibold">Choose Your Payment Plan</h3>
          <p className="text-sm text-muted-foreground font-body">
            Total Amount: <span className="font-mono">AED {amountAED.toLocaleString()}</span>
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {recommendedPlans.map((plan) => {
          const providerName = plan.label?.split(" ")?.[0] ?? "";
          const labels = PROVIDER_LABELS[providerName] ?? [];
          return (
          <Card key={plan.id} className="p-4 hover:shadow-md transition-shadow cursor-pointer border-2 hover:border-primary/20">
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-medium text-lg">{plan.label}</h4>
                  {(() => {
                    const eligibilityAmount = plan.breakdown.bnplAED || amountAED;
                    const tag = estimateEligibility(eligibilityAmount);
                    return (
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${tag==="good"?"bg-green-100 text-green-700":tag==="borderline"?"bg-amber-100 text-amber-700":"bg-gray-100 text-gray-600"}`}>
                        {tag === "good" ? "Likely instant approval" : tag === "borderline" ? "Manual review likely" : "Check eligibility"}
                      </span>
                    );
                  })()}
                </div>
                {labels.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {labels.map((l) => (
                      <BestForLabel key={l} label={l} />
                    ))}
                  </div>
                )}
                {plan.notes && plan.notes.length > 0 && (
                  <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                    {plan.notes.map((note, idx) => (
                      <li key={idx}>• {note}</li>
                    ))}
                  </ul>
                )}
              </div>
              <Button onClick={() => handlePlanSelect(plan)}>
                Select Plan
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              {plan.breakdown.bnplAED && (
                <div className="bg-primary/5 p-3 rounded">
                  <div className="font-medium text-primary font-body">BNPL Amount</div>
                  <div className="text-primary font-mono">AED {plan.breakdown.bnplAED.toLocaleString()}</div>
                </div>
              )}
              
              {plan.breakdown.bankFinanceAED && (
                <div className="bg-flexaura-success/10 p-3 rounded">
                  <div className="font-medium text-flexaura-success font-body">Bank Finance</div>
                  <div className="text-flexaura-success font-mono">AED {plan.breakdown.bankFinanceAED.toLocaleString()}</div>
                </div>
              )}
              
              {plan.breakdown.upfrontAED && (
                <div className="bg-muted p-3 rounded">
                  <div className="font-medium text-foreground font-body">Upfront Payment</div>
                  <div className="text-foreground font-mono">AED {plan.breakdown.upfrontAED.toLocaleString()}</div>
                </div>
              )}
            </div>

            {plan.tenureMonths && (
              <div className="mt-3 text-sm text-muted-foreground">
                Average tenure: {plan.tenureMonths} months
                {plan.aprPercent && ` • APR: ${plan.aprPercent}%`}
              </div>
            )}
          </Card>
          );
        })}
      </div>

      {recommendedPlans.length === 0 && (
        <div className="text-center py-8">
          <p className="text-muted-foreground">No financing plans available for this amount.</p>
          <Button variant="outline" onClick={onBack} className="mt-4">
            Go Back
          </Button>
        </div>
      )}
    </Card>
  );
}

// ─── Travel BNPL checkout (server-routed) ───────────────────────────

type TravelBnplProps = {
  orderId: string;
  amount: number;
  currency?: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  userCountry?: string;
  idempotencyKey?: string;
};

export function TravelBnplProviderSelection(props: TravelBnplProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [routedProvider, setRoutedProvider] = React.useState<string | null>(null);

  async function handleCheckout() {
    try {
      setIsSubmitting(true);
      const result = await createBnplCheckoutSession({
        orderId: props.orderId,
        amount: props.amount,
        currency: props.currency ?? "AED",
        customer: {
          name: props.customerName,
          email: props.customerEmail,
          phone: props.customerPhone,
        },
        vertical: "travel",
        userCountry: props.userCountry,
        idempotencyKey: props.idempotencyKey,
      });
      setRoutedProvider(result.provider);

      // Fire-and-forget referral tracking
      trackReferralClick({
        sessionId: generateSessionId(),
        provider: result.provider,
        vertical: 'travel',
        merchantName: 'Flexaura Travel',
        amount: props.amount,
        currency: props.currency ?? 'AED',
        referrerPage: window.location.pathname,
      });

      window.location.href = result.redirectUrl;
    } catch (err: any) {
      console.error(err);
      toast({
        title: "Could not start BNPL checkout",
        description: err?.message ?? "Please try again or choose another payment option.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="space-y-4 rounded-2xl border p-4 md:p-6">
      <h3 className="text-lg font-heading font-semibold">Pay monthly with a BNPL partner</h3>
      <p className="text-sm text-muted-foreground font-body">
        We'll match you with the best payment provider for your booking amount. You will be redirected to a secure page for KYC and approval.
      </p>

      {props.amount > 15000 && (
        <div className="rounded-xl border border-dashed border-muted-foreground/30 bg-muted/30 p-4">
          <div className="flex items-start gap-3">
            <Building2 className="mt-0.5 h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">Need more time to pay?</p>
              <p className="text-xs text-muted-foreground">
                For larger trips, bank EMI offers extended payment terms up to 24 months.
              </p>
              <Button variant="outline" size="sm" className="mt-2" disabled>
                Coming Soon
              </Button>
            </div>
          </div>
        </div>
      )}

      <Button onClick={handleCheckout} disabled={isSubmitting} className="w-full md:w-auto">
        {isSubmitting ? "Connecting to provider..." : "Continue with BNPL"}
      </Button>

      {routedProvider && (
        <p className="text-xs text-muted-foreground">
          Matched provider: <span className="font-medium capitalize">{routedProvider}</span>
        </p>
      )}

      <p className="mt-2 text-[11px] leading-snug text-muted-foreground">
        Flexaura does not issue credit. All financing is provided by licensed partners such as Tabby and Tamara.
        Approval is subject to their own checks and terms.
      </p>
    </div>
  );
}
